<template>
  <div>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h7">Employee History</span>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <div v-else-if="error" class="error--text text-center py-4">
          {{ error }}
        </div>

        <div v-else-if="historyData && historyData.length > 0">
          <v-timeline align-top dense>
            <v-timeline-item v-for="(entry, index) in historyData" :key="index" small>
              <v-card class="elevation-1">
                <v-card-title class="text-subtitle-1 font-weight-bold">
                  {{ formatDate(entry.promotion_date) }}
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="mb-2">
                        <strong>Status:</strong>
                        <v-chip :color="getStatusColor(entry.status)" small text-color="white">
                          {{ entry.status }}
                        </v-chip>
                      </div>
                      <div class="mb-2">
                        <strong>Reason:</strong> {{ entry.reason_for_promotion || 'N/A' }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="mb-2">
                        <strong>Department:</strong>
                        {{ getDepartmentName(entry.previous_department) }} →
                        {{ getDepartmentName(entry.new_department) }}
                      </div>
                      <div class="mb-2">
                        <strong>Designation:</strong>
                        {{ getDesignationName(entry.previous_designation) }} →
                        {{ getDesignationName(entry.new_designation) }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="mb-2">
                        <strong>Location:</strong>
                        {{ entry.previous_location || 'N/A' }} →
                        {{ entry.new_location || 'N/A' }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="mb-2">
                        <strong>CTC:</strong>
                        {{ formatCurrency(entry.previous_ctc) }} →
                        {{ formatCurrency(entry.new_ctc) }}
                      </div>
                      <div class="mb-2" v-if="entry.increment_percentage">
                        <strong>Increment:</strong> {{ entry.increment_percentage }}%
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="mb-2">
                        <strong>Reporting Manager:</strong>
                        {{ getManagerName(entry.previous_reporting_manager) }} →
                        {{ getManagerName(entry.new_reporting_manager) }}
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </div>

        <div v-else class="text-center py-4">
          <v-icon size="48" color="grey lighten-1">mdi-history</v-icon>
          <p class="mt-2">No history records found for this employee</p>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'EmployeeHistory',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    employee: {
      type: Object,
      required: true
    },
    departments: {
      type: Array,
      default: () => []
    },
    designations: {
      type: Array,
      default: () => []
    },
    managers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dialog: false,
      loading: false,
      error: null,
      historyData: []
    }
  },
  watch: {
    visible(newVal) {
      this.dialog = newVal
      if (newVal) {
        this.fetchEmployeeHistory()
      }
    },
    async employee(newVal) {
      if (newVal) {
        await this.fetchEmployeeHistory()
      }
    }
  },
  async mounted() {
    await this.fetchEmployeeHistory()
  },
  methods: {
    async fetchEmployeeHistory() {
      this.loading = true
      this.error = null

      try {
        const response = await this.$axios.get(`/employee/user/${this.employee._id}`)
        this.historyData = response.data.promotions || []
      } catch (err) {
        this.error = 'Failed to fetch employee history'
        console.error('Error fetching employee history:', err)
      } finally {
        this.loading = false
      }
    },

    closeDialog() {
      this.$emit('handleEmployeeHistoryClose')
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    formatCurrency(amount) {
      if (!amount) return 'N/A'
      return `₹${amount.toLocaleString('en-IN')}`
    },

    getDepartmentName(deptId) {
      if (!deptId) return 'N/A'
      const department = this.departments.find((dept) => dept._id === deptId)
      return department ? department.departmentName : deptId
    },

    getDesignationName(desigId) {
      if (!desigId) return 'N/A'
      const designation = this.designations.find((desig) => desig._id === desigId)
      return designation ? designation.designationName : desigId
    },

    getManagerName(managerId) {
      if (!managerId) return 'N/A'
      const manager = this.managers.find((mgr) => mgr._id === managerId)
      return manager ? `${manager.firstName} ${manager.lastName}` : managerId
    },

    getStatusColor(status) {
      const statusColors = {
        APPROVED: 'success',
        IN_PROGRESS: 'warning',
        HOLD: 'info',
        REJECTED: 'error'
      }
      return statusColors[status] || 'grey'
    }
  }
}
</script>

<style scoped>
.v-timeline-item__dot {
  background-color: #1976d2 !important;
}
</style>
